import type { NavBarItemType, NavbarSection } from "@src/gql/graphql";
import { type ReactNode, createContext, useContext } from "react";

export type LibraryAgentTreeItem = {
	key: string;
	name: string;
	type: NavBarItemType;
	parentKey: string | null;
	hasChildren: boolean;
	isShared: boolean;
	isPublished: boolean;
};

export type LibraryAgentListNavbarTreeResult = {
	section: NavbarSection;
	itemCount: number;
	items: LibraryAgentTreeItem[];
};

export type LibraryAgentCreateWhiteboardResult = {
	whiteboardId: string;
	name: string;
	parentKey: string | null;
};

export type LibraryAgentRenameWhiteboardResult = {
	whiteboardId: string;
	name: string;
};

export type LibraryAgentCreateFolderResult = {
	folderKey: string;
	name: string;
	parentKey: string | null;
};

export type LibraryAgentRenameFolderResult = {
	folderKey: string;
	name: string;
};

export type LibraryAgentDeleteFolderResult = {
	folderKey: string;
	deleted: boolean;
};

export type LibraryAgentMoveNavbarItemResult = {
	movedKeys: string[];
	parentKey: string | null;
};

export type LibraryAgentDuplicateWhiteboardResult = {
	whiteboardId: string;
	name: string;
	sourceWhiteboardId: string;
};

export type LibraryAgentDeleteWhiteboardResult =
	| {
			whiteboardId: string;
			deleted: true;
	  }
	| {
			whiteboardId: string;
			deleted: false;
			error: string;
	  };

export type LibraryAgentActions = {
	listNavbarTree: (input?: {
		section?: NavbarSection;
	}) => Promise<LibraryAgentListNavbarTreeResult>;
	createWhiteboard: (input: {
		name: string;
		parentFolderKey?: string | null;
	}) => Promise<LibraryAgentCreateWhiteboardResult>;
	renameWhiteboard: (input: {
		whiteboardId: string;
		name: string;
	}) => Promise<LibraryAgentRenameWhiteboardResult>;
	createFolder: (input: {
		name: string;
		parentFolderKey?: string | null;
	}) => Promise<LibraryAgentCreateFolderResult>;
	renameFolder: (input: {
		folderKey: string;
		name: string;
	}) => Promise<LibraryAgentRenameFolderResult>;
	deleteFolder: (input: {
		folderKey: string;
	}) => Promise<LibraryAgentDeleteFolderResult>;
	moveNavbarItem: (input: {
		itemKey: string;
		parentKey?: string | null;
		sourceParentKey?: string | null;
		beforeKey?: string | null;
		afterKey?: string | null;
	}) => Promise<LibraryAgentMoveNavbarItemResult>;
	duplicateWhiteboard: (input: {
		whiteboardId: string;
		name?: string;
	}) => Promise<LibraryAgentDuplicateWhiteboardResult>;
	deleteWhiteboard: (input: {
		whiteboardId: string;
	}) => Promise<LibraryAgentDeleteWhiteboardResult>;
};

export type LibraryAgentContextValue = {
	actions: LibraryAgentActions | null;
};

const LibraryAgentContext = createContext<LibraryAgentContextValue | null>(
	null,
);

LibraryAgentContext.displayName = "LibraryAgentContext";

export function LibraryAgentProvider({
	children,
	value,
}: {
	children: ReactNode;
	value: LibraryAgentContextValue;
}) {
	return (
		<LibraryAgentContext.Provider value={value}>
			{children}
		</LibraryAgentContext.Provider>
	);
}

export function useLibraryAgentContext() {
	return useContext(LibraryAgentContext);
}

export function useLibraryAgentActionsRequired() {
	const context = useLibraryAgentContext();
	if (!context?.actions) {
		throw new Error(
			"useLibraryAgentActionsRequired must be used within LibraryAgentProvider",
		);
	}

	return context.actions;
}
